/**
 * shared/api/apiClient.js
 * Generic authenticated API helper used by both frontends.
 * Extracted from PlatformAdmin.js's inline apiCall — now importable anywhere.
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

export class ApiError extends Error {
  constructor(message, { status, statusText, data, contentType, url } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.statusText = statusText;
    this.data = data;
    this.contentType = contentType;
    this.url = url;
  }
}

function isJsonContentType(contentType) {
  return /\bapplication\/json\b|\+json\b/i.test(contentType || "");
}

function isTextContentType(contentType) {
  return /^text\//i.test(contentType || "") ||
    /\b(application\/xml|application\/x-www-form-urlencoded)\b/i.test(contentType || "");
}

function canHaveResponseBody(res) {
  return res.status !== 204 && res.status !== 205;
}

function isNativeBody(body) {
  return (
    (typeof FormData !== "undefined" && body instanceof FormData) ||
    (typeof Blob !== "undefined" && body instanceof Blob) ||
    (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) ||
    (typeof ArrayBuffer !== "undefined" && body instanceof ArrayBuffer)
  );
}

function isBlobValue(value) {
  return typeof Blob !== "undefined" && value instanceof Blob;
}

function buildHeaders(token, optsHeaders, body) {
  const headers = new Headers(optsHeaders || {});

  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (body != null && !isNativeBody(body) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  return headers;
}

async function parseResponseBody(res) {
  if (!canHaveResponseBody(res)) return null;

  const contentType = res.headers.get("content-type") || "";
  if (isJsonContentType(contentType)) {
    const text = await res.text();
    return text ? JSON.parse(text) : null;
  }

  if (isTextContentType(contentType) || !contentType) {
    const text = await res.text();
    if (!text) return null;

    const trimmed = text.trim();
    if (!contentType && (trimmed.startsWith("{") || trimmed.startsWith("["))) {
      try {
        return JSON.parse(trimmed);
      } catch {
        return text;
      }
    }

    return text;
  }

  if (typeof res.blob === "function") {
    return res.blob();
  }

  return res.arrayBuffer();
}

function getErrorMessage(data, res) {
  if (data && typeof data === "object" && !isBlobValue(data)) {
    if (typeof data.error === "string" && data.error.trim()) return data.error;
    if (typeof data.message === "string" && data.message.trim()) return data.message;
  }

  if (typeof data === "string") {
    const text = data.trim();
    const contentType = res.headers.get("content-type") || "";
    if (text && !/html/i.test(contentType) && text.length <= 180) return text;
  }

  return `HTTP ${res.status}${res.statusText ? ` ${res.statusText}` : ""}`;
}

/**
 * apiCall(path, opts, getToken)
 * Makes an authenticated request to the Express backend.
 * getToken should be an async function returning a Firebase ID token string.
 */
export async function apiCall(path, opts = {}, getToken) {
  const token = await getToken();
  const res = await fetch(`${BACKEND}${path}`, {
    ...opts,
    headers: buildHeaders(token, opts.headers, opts.body),
  });

  let data;
  try {
    data = await parseResponseBody(res);
  } catch (err) {
    throw new ApiError("Invalid response from server", {
      status: res.status,
      statusText: res.statusText,
      data: err,
      contentType: res.headers.get("content-type") || "",
      url: res.url,
    });
  }

  if (!res.ok) {
    throw new ApiError(getErrorMessage(data, res), {
      status: res.status,
      statusText: res.statusText,
      data,
      contentType: res.headers.get("content-type") || "",
      url: res.url,
    });
  }

  return data;
}
